<template>
  <v-container fluid class="map-page" ref="pageRef" :style="{ height: pageHeight }">
    <!-- 顶部切换：运力汇总（表格视图）+ 分类（牵引头/挂箱/载货车/临调，地图视图） -->
    <div class="map-tabs">
      <!-- 运力汇总：点击后隐藏地图，切换为表格视图 -->
      <div class="map-tab-item summary-entry" :class="{ active: viewMode === 'summary' }"
        @click="switchView('summary')">
        <v-icon size="15" class="mr-1">mdi-table-large</v-icon>
        <span class="tab-label">运力汇总</span>
      </div>
      <template v-for="tab in tabs">
        <div :key="tab.value" class="map-tab-item" :class="{ active: viewMode === 'map' && tabkey === tab.value }"
          @click="switchMapTab('map', tab.value)">
          <span class="tab-label">{{ tab.label }}</span>
          <span class="tab-count">{{ tabCounts[tab.value] || 0 }}</span>
        </div>
      </template>
      <div class="map-tab-item summary-entry" :class="{ active: viewMode === 'undelivered' }"
        @click="switchView('undelivered')">
        <span class="tab-label">未交付</span>
        <span class="tab-count">{{ tabCounts.undelivered || 0 }}</span>
      </div>
    </div>
    <!-- 地图（列表铺满时由全屏卡片覆盖遮挡，不用 display:none，避免 L7/WebGL 容器尺寸塌缩后无法恢复） -->
    <a-spin v-show="viewMode === 'map'" :spinning="loading" class="map-spin">
      <div class="map-wrapper">
        <div id="map" class="map-box"></div>
        <!-- 车辆详情浮窗（G7 风格：深蓝标题栏 + 指标卡 + 两列信息 + 独立位置区块） -->
        <div v-if="vehicle" class="vehicle-info-panel">
          <div class="vehicle-info-header">
            <div class="header-left">
              <v-icon size="18" color="#fff" class="mr-1">mdi-truck</v-icon>
              <span class="vehicle-plate">{{ vehicle.vehicle_number }}</span>
            </div>
            <a-icon type="close" class="vehicle-close" @click="vehicle = null" />
          </div>
          <div class="vehicle-info-body">
            <!-- 状态徽标 -->
            <div class="status-bar">
              <span class="status-dot" :class="stateClass(vehicle.vehicle_state)"></span>
              <span class="status-text" :class="stateClass(vehicle.vehicle_state)">
                {{ vehicle.vehiclestate || '--' }}</span>
              <span class="status-attr">{{ vehicle.own_type_name }}</span>
            </div>
            <!-- 关键指标 -->
            <div class="metric-group">
              <div class="metric-item">
                <div class="metric-value">{{ vehicle.speed || vehicle.speed === 0 ? vehicle.speed : '--' }}<em>km/h</em>
                </div>
                <div class="metric-label">当前速度</div>
              </div>
              <div class="metric-divider"></div>
              <div class="metric-item">
                <div class="metric-value">
                  {{ vehicle.left_km || vehicle.left_km === 0 ? vehicle.left_km : '--' }}<em>km</em>
                </div>
                <div class="metric-label">剩余里程</div>
              </div>
            </div>
            <!-- 基础信息 -->
            <div class="info-grid">
              <div class="info-cell">
                <span class="info-label">挂箱号</span>
                <span class="info-value">{{ vehicle.vehicle_gnumber || '--' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">车型</span>
                <span class="info-value">{{ vehicle.vehicle_type || '--' }}</span>
              </div>
              <div class="info-cell">
                <span class="info-label">电量</span>
                <span class="info-value">{{ vehicle.soc || '--' }}</span>
              </div>
              <div class="info-cell full">
                <span class="info-label">执行邮路</span>
                <span class="info-value strong">{{ vehicle.postroute || '--' }}</span>
              </div>

            </div>
            <!-- 位置信息 -->
            <div class="info-address">
              <div class="address-head">
                <v-icon size="14" color="#1F6FEB">mdi-map-marker</v-icon>
                <span class="address-time">{{ vehicle.addressTime }}</span>
              </div>
              <p v-if="vehicle.addressTitle" class="address-title">{{ vehicle.addressTitle }}</p>
              <p class="address-text">{{ vehicle.address }}</p>
            </div>
          </div>
        </div>
        <!-- 列表折叠后的展开入口：点击后列表铺满右侧页面 -->
        <div v-if="listCollapsed" class="list-expand-btn" @click="expandListFullscreen">
          <v-icon size="16" color="primary">mdi-chevron-down</v-icon>
          <span>展开列表</span>
        </div>
      </div>
    </a-spin>
    <!-- 查询条件 + 车辆列表（铺满时隐藏地图、列表占满右侧；仅地图视图显示） -->
    <v-card v-show="viewMode === 'map' && !listCollapsed" outlined tile
      :class="['list-card', { 'list-fullscreen': listFullscreen, 'mt-3': !listFullscreen }]">
      <!-- 查询条件：车牌号(挂箱号)、车辆属性、车辆状态、位置（ant 栅格布局，避免 flex 拉伸错位） -->
      <a-row type="flex" align="middle" :gutter="12" class="query-row">
        <!-- 返回地图按钮（铺满模式） -->
        <a-col class="query-col-btn d-flex align-center">
          <v-btn text small class="collapse-btn" @click="backToMap">
            <v-icon small left>mdi-map-outline</v-icon>返回地图
          </v-btn>
        </a-col>
        <a-col :xl="4" :lg="5" :md="6" :sm="12" :xs="24">
          <v-text-field v-model="queryForm.keyword" label="车牌号(挂箱号)" prepend-inner-icon="mdi-magnify" dense outlined
            clearable hide-details @input="search('keyword')">
          </v-text-field>
        </a-col>
        <a-col :xl="4" :lg="5" :md="6" :sm="12" :xs="24">
          <v-autocomplete v-model="queryForm.own_type" :items="own_types" label="车辆属性"
            :menu-props="{ bottom: true, offsetY: true }" no-data-text="无" dense outlined clearable hide-details
            @change="search('own_type')">
          </v-autocomplete>
        </a-col>
        <a-col :xl="4" :lg="5" :md="6" :sm="12" :xs="24">
          <v-autocomplete v-model="queryForm.vehicle_state" :items="vehicle_states" label="车辆状态"
            :menu-props="{ bottom: true, offsetY: true }" no-data-text="无" dense outlined clearable hide-details
            @change="search('vehicle_state')">
          </v-autocomplete>
        </a-col>
        <a-col :xl="4" :lg="5" :md="6" :sm="12" :xs="24">
          <v-autocomplete v-model="queryForm.city" :items="summaryCitys" label="位置"
            :menu-props="{ bottom: true, offsetY: true }" no-data-text="无" dense outlined clearable hide-details
            @change="search('city')">
          </v-autocomplete>
        </a-col>
        <a-col :xl="4" :lg="4" :md="6" :sm="12" :xs="24" class="query-col-actions">
          <v-btn depressed color="primary" @click="getdata()">
            查询
          </v-btn>
          <v-btn outlined class="ml-2" @click="reset()">重置</v-btn>
        </a-col>
      </a-row>
      <!-- 车辆列表：序号、车牌号、挂箱号、车型、车辆属性、司机、执行邮路、剩余里程、速度、状态、位置 -->
      <a-table size="small" :columns="headers" :data-source="slist" :loading="loading" :pagination="false"
        :locale="{ emptyText: '暂无数据' }" :scroll="{ x: width, y: listTableScrollY }" row-key="_id">
        <!-- 列表车辆状态统计条（点击可按状态筛选） -->
        <template slot="title">
          <div class="table-stat-bar">
            <span v-for="s in vehicle_states" :key="s.value" class="stat-item"
              :class="[stateClass(s.value), { active: queryForm.vehicle_state === s.value }]">
              <i class="stat-dot"></i>{{ s.text }}<em>{{ stateCounts[s.value] || 0 }}</em>
            </span>
          </div>
        </template>
        <template slot="index" slot-scope="text, record, index">
          {{ index + 1 }}
        </template>
        <!-- 挂箱 tab 展示主体是挂箱，车牌号不做链接 -->
        <template slot="vehicle_number" slot-scope="text, record">
          <a v-if="tabkey !== 2" href="javascript:;" @click="handleVehicleClick(record)">{{ text }}</a>
          <span v-else>{{ text }}</span>
        </template>
        <!-- 挂箱 tab 下挂箱号为蓝色可点链接（该 tab 展示的主体即挂箱） -->
        <template slot="vehicle_gnumber" slot-scope="text, record">
          <a v-if="tabkey === 2 && text" href="javascript:;" @click="handleVehicleClick(record)">{{ text }}</a>
          <span v-else>{{ text }}</span>
        </template>
        <!-- 车辆属性：自有车/包车/临调 颜色区分 -->
        <template slot="own_type_name" slot-scope="text, record">
          <span :class="['own-type-text', ownTypeClass(record.own_type)]">{{ text }}</span>
        </template>
        <template slot="postroute" slot-scope="text, record">
          <p> {{ record.postroute }} </p>
          <p> {{ record.block_time }} </p>
          <p v-if="record.current_postline">
            {{ record.current_postline }}
          </p>
        </template>
        <template slot="left_km" slot-scope="text">
          {{ text || text === 0 ? text + 'KM' : '' }}
        </template>
        <template slot="speed" slot-scope="text">
          {{ text || text === 0 ? text + 'KM/H' : '' }}
        </template>
        <template slot="vehiclestate" slot-scope="text, record">
          <p>{{ record.vehiclestate }}</p>
          <p>{{ record.vehiclestatetime }}</p>
        </template>
        <template slot="address" slot-scope="text, record">
          <p>{{ record.addressTime }}</p>
          <p>{{ record.address }}</p>
        </template>
      </a-table>
    </v-card>
    <!-- ==================== 运力汇总（表格视图，隐藏地图） ==================== -->
    <v-card v-if="viewMode === 'summary'" outlined tile class="summary-card mt-3">
      <!-- 城市 tab（多一层）：合计 + 河南 18 地市 -->
      <div class="summary-city-tabs">
        <div class="city-tab-item" :class="{ active: summaryForm.city === 'ALL' }" @click="changeSummaryCity('ALL')">
          <span>全部</span>
          <em>{{ summaryCityTotal }}</em>
        </div>
        <div v-for="c in summaryCitys" :key="c.value" class="city-tab-item"
          :class="{ active: summaryForm.city === c.value }" @click="changeSummaryCity(c.value)">
          <span>{{ c.text }}</span>
          <em>{{ c.count }}</em>
        </div>
      </div>
      <!-- 业务状态切换：全部 + 靠位装车/发车/到达待卸/卸车/空车/空放/充电/维修 -->
      <div class="summary-state-bar">
        <span class="state-item" :class="{ active: summaryForm.vehiclestate_v === -1 }" @click="changeSummaryState(-1)">
          全部<em>{{ summaryStateTotal }}</em>
        </span>
        <span v-for="s in summaryCounts" :key="s.value" class="state-item"
          :class="[{ active: summaryForm.vehiclestate_v === s.value }, 'bs-' + s.value]"
          @click="changeSummaryState(s.value)">
          <i class="state-dot"></i>{{ s.text }}<em>{{ s.count }}</em>
        </span>
        <v-spacer></v-spacer>
        <a-space>
          <v-btn-toggle v-model="summaryForm.map_type_name" tile color="deep-purple accent-3" group mandatory>
            <v-btn value="ALL" small>
              全部
            </v-btn>
            <v-btn value="牵引头" small>
              <span class="text-h6" :style="{
                'color': $utils.textToBgColor('牵引头')
              }">{{ mapTypeCounts['牵引头'] }}</span>牵引头
            </v-btn>
            <v-btn value="挂箱" small>
              <span class="text-h6" :style="{
                'color': $utils.textToBgColor('挂箱')
              }">{{ mapTypeCounts['挂箱'] }}</span>挂箱
            </v-btn>
            <v-btn value="载货车" small>
              <span class="text-h6" :style="{
                'color': $utils.textToBgColor('载货车')
              }">{{ mapTypeCounts['载货车'] }}</span>载货车
            </v-btn>
          </v-btn-toggle>
          <v-btn text small color="primary" @click="download()">
            <v-icon small left>mdi-download</v-icon>导出
          </v-btn>
          <v-btn text small @click="getdata()">
            <v-icon small left>mdi-refresh</v-icon>刷新
          </v-btn>
        </a-space>
      </div>
      <!-- 汇总表格：序号、车牌号、车辆类型、车辆性质、司机、电话、状态、位置、更新时间 -->
      <a-input-search placeholder="车牌号/挂厢号/司机" enter-button @input="onInput" @search="onSearch" style="width:100%;" />
      <a-table size="small" :columns="summaryHeaders" :data-source="summaryList" :loading="loading" row-key="_id"
        :pagination="false" :locale="{ emptyText: '暂无数据', filterConfirm: '确认', filterReset: '重置' }"
        :scroll="{ x: summaryWidth, y: summaryTableY }">
        <template slot="index" slot-scope="text, record, index">
          {{ index + 1 }}
        </template>
        <template slot="vehicle_number" slot-scope="text, record">
          <template v-if="record.vehicle_number">
            <p v-if="record.map_type_name" :style="{
              'color': $utils.textToBgColor(record.map_type_name)
            }">{{ record.map_type_name }}</p>
            <span>
              <span :style="{
                'color': $utils.textToBgColor(record.map_type_name)
              }">{{ record.vehicle_number }}</span>
              <span v-if="record.saddle">[{{ record.saddle }}]</span>
            </span>
            <p v-if="record.area" :style="{
              'color': areacolors[record.area]
            }">{{ record.area }}</p>
          </template>
        </template>
        <template slot="vehicle_gnumber" slot-scope="text, record">
          <template v-if="!record.vehicle_number">
            <p v-if="record.map_type_name" :style="{
              'color': $utils.textToBgColor(record.map_type_name)
            }">{{ record.map_type_name }}</p>
            <span>
              <span :style="{
                'color': $utils.textToBgColor(record.map_type_name)
              }">{{ record.vehicle_gnumber }}</span>
              <span v-if="record.gsaddle">[{{ record.gsaddle }}]</span>
            </span>
            <p>{{ record.vehicle_gtype_name }}</p>
            <p v-if="record.area" :style="{
              'color': areacolors[record.area]
            }">{{ record.area }}</p>
          </template>
          <template v-else>
            <span>{{ record.vehicle_gnumber }}<span v-if="record.gsaddle">[{{ record.gsaddle }}]</span></span>
            <p>{{ record.vehicle_gtype_name }}</p>
          </template>
        </template>
        <template slot="driver" slot-scope="text, record">
          <p>{{ record.driver }}</p>
          <p>{{ record.driver_mobile }}</p>
          <p>{{ record.driver_status }}</p>
        </template>
        <template slot="postroute" slot-scope="text, record">
          <v-chip v-if="record.n_rwzt" label small outlined color="error"> {{ record.n_rwzt }} </v-chip>
          <p> {{ record.c_rwsj || record.block_time }} </p>
          <p> {{ record.postroute }} </p>
        </template>
        <template slot="vehiclestate_v" slot-scope="text, record">
          <p v-if="record.current_postline">
            {{ record.current_postline }}
          </p>
          <a-tag :color="vehicleStateColor(record.vehiclestate_v)">{{ record.vehiclestate }}</a-tag>
          <p>{{ record.vehiclestatetime }}</p>
        </template>
        <template slot="address" slot-scope="text, record">
          <p>{{ record.address }}</p>
          <p>{{ record.addressTime }}</p>
        </template>
        <template slot="waybill48" slot-scope="text, record">
          <span v-if="record.waybill48">
            <span>{{ record.waybill48.count }}趟</span>/
            <span>{{ record.waybill48.mileage }}KM</span>
          </span>
        </template>
      </a-table>
    </v-card>
    <!--未交付-->
    <v-card v-if="viewMode === 'undelivered'" outlined tile class="summary-card mt-3">
      <a-table size="small" :columns="headers" :data-source="slist" :loading="loading" :pagination="false"
        :locale="{ emptyText: '暂无数据' }" :scroll="{ x: width, y: listTableScrollY }" row-key="_id">
        <template slot="index" slot-scope="text, record, index">
          {{ index + 1 }}
        </template>
      </a-table>
    </v-card>
  </v-container>
</template>
<script>
import { message } from "ant-design-vue";
import _ from "underscore";
import { Scene, PolygonLayer, PointLayer, GaodeMap } from '@antv/l7';
import ExcelJS from 'exceljs';

export default {
  data() {
    return {
      loading: false,
      // 视图模式：map 地图监控 / summary 运力汇总（汇总时隐藏地图）
      viewMode: 'summary',
      // 底部列表是否折叠（默认折叠、地图全屏；点"展开列表"后列表铺满）
      listCollapsed: true,
      // 列表是否铺满右侧页面（隐藏地图，列表占满）
      listFullscreen: false,
      // ==================== 运力汇总 ====================
      // 汇总查询条件：city 城市（ALL=合计）；business_state 业务状态（''=全部）
      summaryForm: {
        city: 'ALL',
        vehiclestate_v: -1,
        map_type_name: 'ALL',
        matchids: null,
      },
      summaryCitys: [],
      summaryStates: [],
      // 分类 tab：牵引头/挂箱/载货车/临调车 按 shape 过滤，临调按车辆属性过滤
      tabs: [
        { value: 1, label: '牵引头', shape: 'chetou' },
        { value: 2, label: '挂箱', shape: 'guaxiang' },
        { value: 3, label: '载货车', shape: 'zaihuo' },
        { value: 4, label: '临调', shape: 'lindiao' },
      ],
      tabkey: 0,
      tabCounts: {},
      list: [],
      stateCounts: {},
      points: {},
      itemid: 'ALL',
      mapScene: null,
      pointLayer: null,
      circleLayer: null,
      labelLayer: null,
      selectedCircleLayer: null,
      vehicle: null,
      map: {
        center: [114.314521, 22.62032],
        zoom: 6.5,
      },
      // ==================== 地图聚合（城市-省-国三级趋势） ====================
      clusterLayer: null,
      clusterLabelLayer: null,
      clusterZoomThreshold: 10,
      isClusterView: false,
      cityProvinceMap: {
        '郑州': '河南省', '开封': '河南省', '洛阳': '河南省', '平顶山': '河南省',
        '安阳': '河南省', '鹤壁': '河南省', '新乡': '河南省', '焦作': '河南省',
        '濮阳': '河南省', '许昌': '河南省', '漯河': '河南省', '三门峡': '河南省',
        '南阳': '河南省', '商丘': '河南省', '信阳': '河南省', '周口': '河南省',
        '驻马店': '河南省', '济源': '河南省',
      },
      districtCityMap: {
        '管城回族区': '郑州',
        '航空港区': '郑州',
      },
      // 列表查询表单
      queryForm: {
        keyword: '',
        own_type: undefined,
        vehicle_state: undefined,
        city: undefined,
      },
      showsearchresult: false,
      // 车辆属性下拉项（v-autocomplete 使用 text/value 结构）
      own_types: [
        { value: 1, text: '自有车' },
        { value: 3, text: '包车' },
        { value: 2, text: '临调' },
      ],
      // 车辆状态下拉项
      vehicle_states: [
        { value: 1, text: '行驶中', },
        { value: 2, text: '停车中', },
        { value: 3, text: '离线', },
      ],
      // 可视区高度，用于表格高度自适应
      windowHeight: window.innerHeight,
      // 页面容器高度（动态测量顶部偏移后计算，保证底部分页可见）
      pageHeight: 'auto',
      // 页面容器高度数值（px），供铺满模式计算表格滚动区高度
      pageHeightNum: 0,
      areacolors: {
        '南信周': '#D84141',
        '商丘开封': '#D8C341',
        '安濮鹤新': '#6CD841',
        '机动车辆': '#41D897',
        '洛焦济三': '#4197D8',
        '漯平许驻': '#6C41D8',
        '豫东豫北': '#D841C3'
      },
    }
  },
  components: {},
  computed: {
    headers() {

      return this.viewMode === 'map' ? [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 70, align: "center" },
        { title: "车牌号", dataIndex: "vehicle_number", scopedSlots: { customRender: "vehicle_number" }, width: 120, align: "center" },
        ...((this.tabkey === 1 || this.tabkey === 3) ? [
          { title: "电量", dataIndex: "soc", width: 80, align: "center" },
        ] : []),
        { title: "挂箱号", dataIndex: "vehicle_gnumber", scopedSlots: { customRender: "vehicle_gnumber" }, width: 120, align: "center" },
        { title: "车辆吨位", dataIndex: "vehicle_type", width: 120, align: "center" },
        { title: "车辆属性", dataIndex: "own_type_name", scopedSlots: { customRender: "own_type_name" }, width: 100, align: "center" },
        { title: "司机", dataIndex: "driver", width: 100, align: "center" },
        { title: "执行邮路", dataIndex: "postroute", scopedSlots: { customRender: "postroute" }, width: 160, align: "center" },
        { title: "剩余里程", dataIndex: "left_km", scopedSlots: { customRender: "left_km" }, width: 110, align: "center" },
        { title: "速度", dataIndex: "speed", scopedSlots: { customRender: "speed" }, width: 110, align: "center" },
        { title: "状态", dataIndex: "vehiclestate", scopedSlots: { customRender: "vehiclestate" }, width: 100, align: "center" },
        { title: "位置", dataIndex: "address", scopedSlots: { customRender: "address" }, align: "center" },
      ] : [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 70, align: "center" },
        { title: "车牌号", dataIndex: "vehicle_number", scopedSlots: { customRender: "vehicle_number" }, width: 120, align: "center" },
        { title: "车辆吨位", dataIndex: "vehicle_type", width: 120, align: "center" },
        { title: "车辆性质", dataIndex: "own_type_name", width: 120, align: "center" },
        { title: "状态", dataIndex: "deliver_state", width: 120, align: "center" },
        { title: "交付日期", dataIndex: "deliver_date", width: 120, align: "center" },
        { title: "备注", dataIndex: "deliver_remark", width: 200, align: "center" },
      ];
    },
    summaryHeaders() {
      return [
        { title: '序号', dataIndex: '_sindex', scopedSlots: { customRender: 'index' }, width: 70, align: 'center' },
        { title: '车牌号', dataIndex: 'vehicle_number', scopedSlots: { customRender: 'vehicle_number' }, width: 120, align: 'center' },
        { title: '挂厢号', dataIndex: 'vehicle_gnumber', scopedSlots: { customRender: 'vehicle_gnumber' }, width: 120, align: 'center' },
        { title: "电量", dataIndex: 'soc', width: 80, align: "center" },
        { title: '车辆吨位', dataIndex: 'vehicle_type', width: 100, align: 'center', },
        {
          title: '车辆性质', dataIndex: 'own_type_name', width: 100, align: 'center',
          filters: _.uniq(this.list.map(n => n.own_type_name)).map(n => ({
            text: n,
            value: n,
          })),
          onFilter: (value, record) => record.own_type_name === value,
        },
        { title: '司机', dataIndex: 'driver', scopedSlots: { customRender: 'driver' }, width: 120, align: 'left' },
        { title: "执行邮路", dataIndex: "postroute", scopedSlots: { customRender: 'postroute' }, width: 180, align: "left" },
        { title: '状态', dataIndex: 'vehiclestate_v', scopedSlots: { customRender: 'vehiclestate_v' }, width: 160, align: 'left' },
        { title: '位置', dataIndex: 'address', scopedSlots: { customRender: 'address' }, ellipsis: true, align: 'left' },
        { title: '48H任务', dataIndex: 'waybill48', scopedSlots: { customRender: 'waybill48' }, width: 100, align: 'left' },
      ];
    },
    width() {
      return _.reduce(this.headers, (a, b) => a + (b.width || 300), 0);
    },
    // 表格滚动区高度：随视口高度自适应，约占 20%，为底部分页留出空间
    tableScrollY() {
      const h = Math.round(this.windowHeight * 0.2);
      return Math.min(Math.max(h, 150), 320);
    },
    // 列表实际滚动区高度：铺满模式下占满右侧剩余高度（无分页，预留 tab/查询行/状态条/表头）
    listTableScrollY() {
      if (!this.listFullscreen && this.viewMode !== 'undelivered') return this.tableScrollY;
      const base = this.pageHeightNum || (this.windowHeight - 110);
      return Math.max(base - 200, 280);
    },
    slist() {
      if (this.showsearchresult) {
        let {
          keyword,
          own_type,
          vehicle_state,
          city,
        } = this.queryForm;
        let match_keyword = (n, v) => n.vehicle_number?.includes(v) || n.vehicle_gnumber?.includes(v);
        let match_own_type = (n, v) => n.own_type === v;
        let match_vehicle_state = (n, v) => n.vehicle_state === v;
        return this.list.filter(n => {
          return (
            (!keyword || match_keyword(n, keyword))
            &&
            (!own_type || match_own_type(n, own_type))
            &&
            (!vehicle_state || match_vehicle_state(n, vehicle_state))
            &&
            (!city || n.city === city || (city === '其他' && !n.city))
          )
        });
      }
      else return this.list;
    },
    // ==================== 运力汇总 ====================
    summaryWidth() {
      return _.reduce(this.summaryHeaders, (a, b) => a + (b.width || 240), 0);
    },
    // 汇总视图无地图，表格占据剩余高度（预留状态条 + 城市 tab 两层高度）
    summaryTableY() {
      return Math.max(this.windowHeight - 350, 300);
    },
    // 全部车辆数（合计角标）
    summaryCityTotal() {
      return this.summaryCitys.reduce((a, b) => a + (b.ignore_total_count ? 0 : b.count), 0);
    },
    // 当前城市车辆数（状态条"全部"角标）
    summaryCityList() {
      return this.list.filter(n => {
        return (
          this.summaryForm.city === 'ALL' || n.city === this.summaryForm.city || (this.summaryForm.city === '其他' && !n.city)
        )
      });
    },
    summaryCounts() {
      console.log('summaryCounts', this.summaryCityList.length);
      return this.summaryStates.map(n => {
        return {
          ...n,
          count: this.summaryCityList.filter(m => m.vehiclestate_v === n.value).length,
        };
      });
    },
    summaryStateTotal() {
      return this.summaryCounts.reduce((a, b) => a + b.count, 0);
    },
    summaryList() {
      return this.list.filter(n => {
        return (
          this.summaryForm.city === 'ALL' || n.city === this.summaryForm.city || (this.summaryForm.city === '其他' && !n.city)
        )
          && (
            this.summaryForm.vehiclestate_v === -1 || n.vehiclestate_v === this.summaryForm.vehiclestate_v
          )
          && (
            this.summaryForm.map_type_name === 'ALL' || this.summaryForm.map_type_name === n.map_type_name
          )
          && (
            !this.summaryForm.matchids || this.summaryForm.matchids.includes(n._id)
          )
      });
    },
    mapTypeCounts() {
      let result = {
        '牵引头': 0,
        '挂箱': 0,
        '载货车': 0,
      };
      this.summaryList.forEach(n => result[n.map_type_name]++);
      return result;
    },
  },
  watch: {
  },
  mounted() {
    this.changeTab(0);
    window.addEventListener('resize', this.onResize);
    this.updatePageHeight();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    // 依据容器在页面中的实际顶部偏移计算可用高度，避免底部分页被挤出可视区
    updatePageHeight() {
      this.$nextTick(() => {
        const el = this.$refs.pageRef?.$el || this.$refs.pageRef;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        this.pageHeightNum = Math.max(window.innerHeight - top - 16, 420);
        this.pageHeight = `${this.pageHeightNum}px`;
      });
    },
    // 窗口尺寸变化时同步高度，并让地图重新计算尺寸
    onResize() {
      this.windowHeight = window.innerHeight;
      this.updatePageHeight();
      this.$nextTick(() => {
        this.mapScene?.map?.resize?.();
      });
    },
    // 展开列表并铺满右侧页面：列表卡片覆盖在地图上方
    expandListFullscreen() {
      this.vehicle = null;
      this.listCollapsed = false;
      this.listFullscreen = true;
    },
    // 从铺满列表返回地图：收起列表恢复地图全屏
    backToMap() {
      this.listFullscreen = false;
      this.listCollapsed = true;
      this.$nextTick(() => {
        setTimeout(() => this.mapScene?.map?.resize?.(), 300);
      });
    },
    // 车辆状态对应的样式类：行驶中/停车中/离线
    stateClass(state) {
      if (state === 1) return 'is-running';
      if (state === 2) return 'is-stop';
      if (state === 3) return 'is-offline';
      return '';
    },
    changeTab(v) {
      this.tabkey = v;
      this.vehicle = null;
      this.itemid = 'ALL';
      this.getdata();
    },
    // 顶部视图切换：map 地图监控 / summary 运力汇总
    switchView(mode) {
      this.viewMode = mode;
      this.vehicle = null;
      // 离开地图视图时退出列表铺满
      this.listFullscreen = false;
      this.listCollapsed = true;
      if (mode === 'summary') {
        // 首次进入汇总视图时加载数据
        this.changeTab(0);
      }
      else if (mode === 'undelivered') {
        this.changeTab(99);
      }
      else {
        // 切回地图：等待布局恢复后重绘地图，避免容器尺寸为 0
        this.$nextTick(() => setTimeout(() => this.mapScene?.map?.resize?.(), 300));
      }
    },
    // 点击牵引头/挂箱等分类：切回地图视图并切换分类
    switchMapTab(mode, v) {
      this.viewMode = mode;
      this.initMap();
      this.changeTab(v);
      this.$nextTick(() => setTimeout(() => this.mapScene?.map?.resize?.(), 300));
    },
    // ==================== 运力汇总 ====================
    onInput(e) {
      let value = e.data;
      console.log('onInput', value);
      if (!value) {
        this.summaryForm.matchids = null;
      }
    },
    onSearch(value) {
      console.log('onSearch', value);
      this.summaryForm.matchids = this.summaryList.filter(n => {
        if (value) {
          return (
            (
              n.vehicle_number && n.vehicle_number.includes(value)
            )
            ||
            (
              n.vehicle_gnumber && n.vehicle_gnumber.includes(value)
            )
            ||
            (
              n.driver && n.driver.includes(value)
            )
          )
        }
        return true;
      }).map(n => n._id);
    },
    vehicleStateColor(v) {
      return {
        0: 'pink',      // 等待任务
        1: 'blue',      // 靠位装车
        2: 'cyan',      // 发车
        3: 'orange',    // 到达待卸
        4: 'gold',      // 卸车
        5: 'gray',   // 空车
        6: 'purple',    // 空放
        7: 'green',     // 充电
        8: 'red',       // 修车
      }[v] || 'black';
    },
    changeSummaryCity(city) {
      console.log(city);
      this.summaryForm.city = city;
    },
    changeSummaryState(state) {
      // 再次点击同一状态则取消筛选
      this.summaryForm.vehiclestate_v = state;
    },
    search() {
      this.showsearchresult = true;
    },
    reset() {
      this.queryForm = {
        keyword: '',
        own_type: undefined,
        vehicle_state: undefined,
        city: undefined
      };
      this.showsearchresult = false;
    },
    // ==================== 地图 ====================
    initMap() {
      if (this.mapScene) return;
      this.loading = true;
      const scene = new Scene({
        id: 'map',
        preserveDrawingBuffer: true,
        map: new GaodeMap({
          center: this.map.center,
          style: 'light',
          pitchEnable: false,
          token: 'df486db18336b76e44d3d80a0cd23ce6',
          zoom: this.map.zoom,
        }),
      });
      this.mapScene = scene;
      // 聚合：zoomchange 切换可见性（轻量），zoomend 重建图层数据（避免动画中断）
      scene.on('zoomchange', () => {
        this.updateClusterView();
      });
      scene.on('zoomend', () => {
        clearTimeout(this._clusterRebuildTimer);
        if (this.isClusterView && this._cityClusters) {
          this.rebuildClusterLayers();
        }
      });
      scene.on('loaded', async () => {
        scene.addImage('chetou', this.$constant.chetou);//牵引头
        scene.addImage('guaxiang', this.$constant.guaxiang);//挂箱
        scene.addImage('zaihuo', this.$constant.zaihuo);//载货车
        // 租车专用图标：挂箱租车=baoche，牵引头租车=baocheyitiche，载货车租车=zaihuobaoche
        scene.addImage('baoche', this.$constant.baoche);
        scene.addImage('baocheyitiche', this.$constant.baocheyitiche);
        scene.addImage('lindiao', this.$constant.baocheyitiche);
        scene.addImage('zaihuobaoche', this.$constant.zaihuobaoche);
        this.getdata();
      });
    },
    getdata() {
      this.loading = true;
      this.$api.doAPI("/map/list2", { tab: this.tabkey }).then((res) => {
        if (res.code === 0) {
          this.list = res.data.list;
          this.tabCounts = res.data.tabCounts;
          this.summaryCitys = res.data.summaryCitys;
          this.summaryStates = res.data.summaryStates;
          if (this.viewMode === 'summary') {
            this.$set(this.summaryForm, 'city', 'ALL');
            this.$set(this.summaryForm, 'vehiclestate_v', -1);
          }
          if (this.viewMode === 'map') {
            if (this.pointLayer) {
              this.pointLayer.off("click");
              this.mapScene?.removeLayer(this.pointLayer);
            }
            if (this.circleLayer) {
              this.mapScene?.removeLayer(this.circleLayer);
            }
            if (this.labelLayer) {
              this.mapScene?.removeLayer(this.labelLayer);
            }
            if (this.selectedCircleLayer) {
              this.mapScene?.removeLayer(this.selectedCircleLayer);
            }
            // 清理旧聚合图层
            if (this.clusterLayer) {
              this.mapScene?.removeLayer(this.clusterLayer);
              this.clusterLayer = null;
            }
            if (this.clusterLabelLayer) {
              this.mapScene?.removeLayer(this.clusterLabelLayer);
              this.clusterLabelLayer = null;
            }
            this.isClusterView = false;
            this.stateCounts = res.data.stateCounts;
            let labels = [];
            this.points = res.data.points;
            // 构建 _id -> 车辆属性 映射（列表行与点位同属一条 MapLocation，_id 一致，最可靠），车牌/挂箱号兜底
            this.ownTypeMap = {};
            (this.list || []).forEach(n => {
              if (n._id) this.ownTypeMap[n._id] = n.own_type;
              if (n.vehicle_number) this.ownTypeMap[n.vehicle_number] = n.own_type;
              if (n.vehicle_gnumber) this.ownTypeMap[n.vehicle_gnumber] = n.own_type;
            });
            // 租车（非自有）切换专用图标：挂箱 guaxiang->baoche，牵引头 chetou->baocheyitiche，载货车 zhengche->zaihuobaoche
            (this.points?.features || []).forEach(f => {
              const p = f.properties;
              console.log(p);
              if (!p || (p.shape !== 'chetou' && p.shape !== 'guaxiang' && p.shape !== 'zaihuo' && p.shape !== 'lindiao')) return;
              let ownType = p.own_type;
              if ((ownType === undefined || ownType === null || ownType === '') && this.ownTypeMap) {
                ownType = this.ownTypeMap[p._id] ?? this.ownTypeMap[p.name];
              }
              // 仅包车(3)切换租车图标；自有车(1)、临调(2)及其他保持原图标
              if (ownType === undefined || ownType === null || ownType === '') return;
              if (Number(ownType) !== 3) return;
              console.log(p.shape, ownType);
              if (p.shape === 'guaxiang') p.shape = 'baoche';
              else if (p.shape === 'chetou') p.shape = 'baocheyitiche';
              else if (p.shape === 'zaihuo' || p.shape === 'lindiao') p.shape = 'zaihuobaoche';
            });
            //图标图层
            if (res.data.points?.features?.length > 0) {
              this.pointLayer = new PointLayer({ zIndex: 10 })
                .source(res.data.points)
                .shape('shape')
                // 用预计算的 _size 字段驱动 size，便于"选中图标放大"即时生效
                .size('_size')
                .style({
                  opacity: 1,
                  // 选中时橙色描边
                  stroke: '#FF6B00',
                  strokeWidth: (item) => {
                    return item?.feature?.properties?._selected ? 2 : 0;
                  },
                });
              this.pointLayer.on("click", (e) => {
                const { feature } = e;
                if (feature?.properties) {
                  // 地图点被点击时同步选中态
                  this.itemid = feature.properties._id || null;
                  this.refreshSelectedMarkerSize();
                  this.showData(feature.properties);
                }
              });
              this.mapScene.addLayer(this.pointLayer);
              // 首次加载时根据当前 itemid 预计算 _size / _selected
              this.refreshSelectedMarkerSize();
              labels.push(...res.data.points.features.map(n => {
                return {
                  m: n.properties.name,
                  w: Number(n.properties.latitude),
                  j: Number(n.properties.longitude),
                  t: 10,
                  s: "河南",
                  _id: n.properties._id,
                  _name: n.properties.name,
                  // 自有车/租车标签分色（选中态在方法内处理）
                  _color: this.vehicleLabelColor(n.properties, false),
                };
              }))
            }
            //邮局周围方圆
            if (res.data.circles?.features?.length > 0) {
              this.circleLayer = new PolygonLayer({ zIndex: 0 })
                .source(res.data.circles)
                .size(2)
                .shape("line")
                .color("red")
                .style({
                  opacity: 0.2,
                });
              this.mapScene.addLayer(this.circleLayer);
            }
            // 选中专用圆环图层：zIndex 15，介于 pointLayer (10) 和 labelLayer (20) 之间
            this.selectedCircleLayer = new PointLayer({ zIndex: 15 }).source([], {
              parser: {
                type: 'json',
                x: 'j',
                y: 'w',
              },
            }).shape('circle').size(60).color('#FF6B00').style({
              opacity: 0.25, // 半透明填充
              stroke: '#FF6B00', // 描边色
              strokeWidth: 2, // 描边宽度
              strokeOpacity: 0.9, // 描边不透明
            });
            this.mapScene.addLayer(this.selectedCircleLayer);
            //标签
            if (labels.length > 0) {
              this.labelLayer = new PointLayer({ zIndex: 20 })
                .source(labels, {
                  parser: {
                    type: 'json',
                    x: 'j',
                    y: 'w',
                  },
                })
                .shape('m', 'text')
                .size(12)
                // 用 _color 字段驱动颜色：选中车辆的车牌变橙红，未选中保持深蓝
                .color('_color')
                .style({
                  textAnchor: 'center', // 文本相对锚点的位置 center|left|right|top|bottom|top-left
                  textOffset: [0, 30], // 文本相对锚点的偏移量 [水平, 垂直]
                  spacing: 2, // 字符间距
                  padding: [0, 0], // 文本包围盒 padding 改为 0，避免密集车辆 label 被碰撞检测剔除
                  stroke: '#ffffff', // 描边颜色
                  strokeWidth: 2, // 描边宽度
                  strokeOpacity: 1.0,
                  rotation: {
                    // 字段映射旋转
                    field: 't',
                    value: [0, 0],
                  },
                });
              this.mapScene.addLayer(this.labelLayer);
            }
            this.refreshSelectedMarkerSize();
            // 从列表数据补全点位的 city 字段（后端点位未含 city，通过 _id 匹配列表行）
            const cityById = {};
            (this.list || []).forEach(n => {
              if (n._id && n.city) cityById[n._id] = n.city;
            });
            (this.points?.features || []).forEach(f => {
              const p = f.properties;
              if (p && (!p.city) && cityById[p._id]) {
                p.city = cityById[p._id];
              }
            });
            // 构建聚合数据 + 初始化聚合视图（非缩放动画中，立即渲染当前层级）
            this.buildClusterLayers();
            this.updateClusterView();
            if (this.isClusterView) {
              clearTimeout(this._clusterRebuildTimer);
              this.rebuildClusterLayers();
            }
            setTimeout(() => {
              if (res.data.center) {
                this.mapScene.setCenter(res.data.center);
              }
            }, 100);
          }
        }
        else {
          message.error(res.msg);
        }
        this.loading = false;
      }).catch((err) => {
        this.loading = false;
        console.error(err);
      });
    },
    async download() {
      let rows = [
        [
          "序号",
          "车辆类型",
          "车牌号",
          "挂厢号",
          "电量",
          "车辆吨位",
          "车辆性质",
          "司机姓名",
          "司机电话",
          "任务状态",
          "任务时间",
          "执行邮路",
          "报备线路",
          "报备状态",
          "报备时间",
          "位置",
          "更新时间",
          "48H任务趟次",
          "48H任务里程(KM)",
        ],
        ...this.summaryList.map((n, i) => [
          ++i,
          n.map_type_name || '',
          n.vehicle_number || '',
          n.vehicle_gnumber || '',
          n.soc || '',
          n.vehicle_type || '',
          n.own_type_name || '',
          n.driver || '',
          n.driver_mobile || '',
          n.n_rwzt || '',
          n.c_rwsj || n.block_time || '',
          n.postroute || '',
          n.current_postline || '',
          n.vehiclestate || '',
          n.vehiclestatetime || '',
          n.address || '',
          n.addressTime || '',
          n.waybill48?.count || '',
          n.waybill48?.mileage || '',
        ]),
      ];
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');
      worksheet.addRows(rows);
      const buffer = await workbook.xlsx.writeBuffer();
      this.$utils.downloadBlob([buffer], "application/octet-stream", `运力汇总.xlsx`);
    },
    // 车辆属性样式：1 自有车(蓝) / 3 包车(橙) / 2 临调(紫)
    ownTypeClass(v) {
      if (v === 1) return 'is-self';
      if (v === 3) return 'is-charter';
      if (v === 2) return 'is-temp';
      return '';
    },
    // 地图标签（车牌名）颜色：
    // 选中统一橙红高亮；挂箱/牵引头下未选中时，自有车深蓝、租车（非自有）#e16531；
    // 自有车深蓝 #084081 / 租车橙 #e16531 / 选中橙红 #FF4500
    vehicleLabelColor(props, isSel) {
      if (isSel) return '#FF4500';
      const shape = props && props.shape;
      if (shape === 'chetou' || shape === 'guaxiang' || shape === 'baoche' || shape === 'baocheyitiche' || shape === 'lindiao' || shape === 'zaihuobaoche') {
        let ownType = props && props.own_type;
        if ((ownType === undefined || ownType === null || ownType === '') && this.ownTypeMap) {
          ownType = this.ownTypeMap[props && props._id] ?? this.ownTypeMap[props && props.name];
        }
        // 仅包车(3)橙色；自有车(1)、临调(2)及其他保持默认深蓝
        if (ownType === undefined || ownType === null || ownType === '') return '#084081';
        return Number(ownType) === 3 ? '#e16531' : '#084081';
      }
      return '#084081';
    },
    // 表格里点击车牌号/挂箱号：地图定位 + 弹出车辆信息浮窗
    handleVehicleClick(item) {
      if (!item) return;
      // 铺满列表下点击：先收起列表回到地图，待 DOM 切换后再定位弹窗
      if (this.listFullscreen) {
        this.backToMap();
        this.$nextTick(() => this.locateVehicle(item));
      }
      else {
        this.locateVehicle(item);
      }
    },
    // 定位车辆点位并弹出浮窗（供车牌号、挂箱号点击复用）
    locateVehicle(item) {
      if (this.mapScene && Number.isFinite(Number(item.longitude)) && Number.isFinite(Number(item.latitude))) {
        const lng = Number(item.longitude);
        const lat = Number(item.latitude);
        // 聚合层在 zoom<10 时只显示聚合圆，看不到单车点；定位时放大到街道级（14）以看清车辆点和周边路网
        const targetZoom = 14;
        // L7 Scene 无 setZoomAndCenter，用 AMap 原生方法（带平滑动画）；高德 zoom 比 L7 zoom 大 1，需 +1
        const amap = this.mapScene.map;
        if (amap && amap.setZoomAndCenter) {
          amap.setZoomAndCenter(targetZoom + 1, [lng, lat]);
        }
        else {
          this.mapScene.setZoom(targetZoom);
          this.mapScene.setCenter([lng, lat]);
        }
      }
      // 优先按 _id 精确匹配当前 tab 的地图点位（挂箱 tab 点的是挂箱号），
      // 再回退按车牌号/挂箱号名称匹配；匹配不到时直接展示列表行数据
      const features = this.points?.features || [];
      const point = features.find(n => n.properties?._id === item._id)
        || features.find(n => n.properties?.name === item.vehicle_number)
        || features.find(n => n.properties?.name === item.vehicle_gnumber);
      if (point) {
        this.itemid = point.properties._id;
        this.refreshSelectedMarkerSize();
        this.showData(point.properties);
      }
      else {
        this.vehicle = { ...item };
      }
    },
    // 重新计算每个 feature 的 _size（基础/放大）和 _selected（描边）字段
    refreshSelectedMarkerSize() {
      if (!this.pointLayer || !this.points?.features?.length) return;
      const features = this.points.features.map((f) => {
        const props = f.properties || {};
        const shape = props.shape;
        let base = (shape === 'chetou' || shape === 'guaxiang' || shape === 'zaihuo' || shape === 'lindiao') ? 28 : 16;
        base = 15;
        const isSel = this.itemid && props._id === this.itemid;
        return {
          ...f,
          properties: {
            ...props,
            _size: isSel ? Math.round(base * 1.2) : base,
            _selected: isSel ? 1 : 0,
          },
        };
      });
      this.pointLayer.setData({ ...this.points, features });

      // 同步 labelLayer：选中车辆的车牌号变橙红；未选中时挂箱/牵引头按自有/租车分色
      if (this.labelLayer && this.points.features) {
        const labelFeatures = this.points.features.map((f) => {
          const props = f.properties || {};
          const isSel = this.itemid && props._id === this.itemid;
          return {
            m: props.name,
            w: Number(props.latitude),
            j: Number(props.longitude),
            t: 10,
            s: "河南",
            _id: props._id,
            _name: props.name,
            _color: this.vehicleLabelColor(props, isSel),
          };
        });
        this.labelLayer.setData(labelFeatures);
      }

      // 同步 selectedCircleLayer：选中车辆的位置绘制一个橙色圆环
      if (this.selectedCircleLayer) {
        const sel = features.find((f) => f.properties._selected === 1);
        const circleData = sel ? [{
          j: Number(sel.properties.longitude),
          w: Number(sel.properties.latitude),
        }] : [];
        this.selectedCircleLayer.setData(circleData);
      }
    },
    showData(params) {
      this.vehicle = this.list.find(n => n._id === params._id);
      // this.loading = true;
      // this.$api.doAPI("/map/show", params).then((res) => {
      //   this.loading = false;
      //   if (res.code === 0) {
      //     this.vehicle = res.data.data;
      //   }
      //   else {
      //     message.error(res.msg);
      //   }
      // }).catch((err) => {
      //   this.loading = false;
      // })
    },
    // ==================== 地图聚合（城市-省-国三级趋势） ====================
    // 城市名规范化：管城回族区/航空港区 -> 郑州（仅聚合用，不影响后端业务统计）
    normalizeCityName(city) {
      if (!city) return '未知';
      if (this.districtCityMap[city]) return this.districtCityMap[city];
      return city;
    },
    // 构建城市级聚合数据：按城市分组，计算质心+数量+省份
    buildClusterLayers() {
      const features = this.points?.features || [];
      const cityMap = {};
      const unknownPts = [];
      features.forEach(f => {
        const p = f.properties || {};
        const rawCity = p.city ? this.normalizeCityName(p.city) : '';
        const pt = p.longitude && p.latitude ? [Number(p.longitude), Number(p.latitude)] : null;
        // 无城市信息的点先收集，稍后归入最近的已知城市
        if (!rawCity) {
          if (pt) unknownPts.push(pt);
          return;
        }
        if (!cityMap[rawCity]) {
          cityMap[rawCity] = { city: rawCity, count: 0, lngs: [], lats: [], province: this.cityProvinceMap[rawCity] || '其他' };
        }
        cityMap[rawCity].count++;
        if (pt) {
          cityMap[rawCity].lngs.push(pt[0]);
          cityMap[rawCity].lats.push(pt[1]);
        }
      });
      // 已知城市初步质心
      const groups = Object.values(cityMap);
      groups.forEach(c => {
        c.lng = c.lngs.length ? c.lngs.reduce((a, b) => a + b, 0) / c.lngs.length : 113.6;
        c.lat = c.lats.length ? c.lats.reduce((a, b) => a + b, 0) / c.lats.length : 34.0;
      });
      // 无城市信息的点归入距离最近的已知城市（没有已知城市时才保留"未知"）
      if (unknownPts.length) {
        if (!groups.length) {
          groups.push({ city: '未知', province: '其他', count: 0, lngs: [], lats: [], lng: 113.6, lat: 34.0 });
        }
        unknownPts.forEach(([lng, lat]) => {
          let nearest = groups[0];
          let minDist = Infinity;
          groups.forEach(c => {
            const d = (c.lng - lng) ** 2 + (c.lat - lat) ** 2;
            if (d < minDist) { minDist = d; nearest = c; }
          });
          nearest.count++;
          nearest.lngs.push(lng);
          nearest.lats.push(lat);
        });
      }
      // 重新计算最终质心
      const cityClusters = groups.map(c => ({
        city: c.city,
        province: c.province,
        count: c.count,
        lng: c.lngs.length ? c.lngs.reduce((a, b) => a + b, 0) / c.lngs.length : c.lng,
        lat: c.lats.length ? c.lats.reduce((a, b) => a + b, 0) / c.lats.length : c.lat,
      }));
      this._cityClusters = cityClusters;
    },
    // 根据当前 zoom 重建聚合图层：省级(<5) / 城市级(5-10) / 单车(>=10)
    // 省/市分界取 5：高德底图 L7<5（AMap<6）只显示省名/省会，不显示地级市名
    rebuildClusterLayers() {
      if (!this._cityClusters || !this._cityClusters.length) return;
      const zoom = this.mapScene.getZoom();
      let display = [];
      if (zoom < 5) {
        // ========== 省级（最低聚合级）：底图不显示城市名时，每省一个 省名(省总数) ==========
        const provMap = {};
        this._cityClusters.forEach(c => {
          if (!provMap[c.province]) provMap[c.province] = { province: c.province, count: 0, lngs: [], lats: [] };
          provMap[c.province].count += c.count;
          provMap[c.province].lngs.push(c.lng);
          provMap[c.province].lats.push(c.lat);
        });
        Object.values(provMap).forEach(p => {
          const lng = p.lngs.reduce((a, b) => a + b, 0) / p.lngs.length;
          const lat = p.lats.reduce((a, b) => a + b, 0) / p.lats.length;
          display.push({
            j: lng, w: lat,
            m: `${p.province}(${p.count})`,
            _circleSize: 50, _fillColor: '#FFE3CC', _textColor: '#d4500e',
          });
        });
      } else {
        // ========== 城市级：城市名(数量) ==========
        if (zoom < 10) {
          // 合并距离按屏幕像素恒定（约40px）换算为地理度数，低 zoom 自动合并邻近城市避免重叠
          // 高德 zoom 比 L7 大 1，纬度约 35°，1px 对应度数 = 360/(256*2^(zoom+1))*cos(35°)
          const mergeDist = 40 * 360 * 0.82 / (256 * Math.pow(2, zoom + 1));
          const merged = [];
          const used = new Set();
          const sorted = [...this._cityClusters].sort((a, b) => b.count - a.count);
          sorted.forEach((c, i) => {
            if (used.has(i)) return;
            let group = { ...c };
            sorted.forEach((d, j) => {
              if (i === j || used.has(j)) return;
              const dist = Math.sqrt((c.lng - d.lng) ** 2 + (c.lat - d.lat) ** 2);
              if (dist < mergeDist) {
                group.count += d.count;
                used.add(j);
              }
            });
            used.add(i);
            merged.push(group);
          });
          merged.forEach(c => {
            display.push({
              j: c.lng, w: c.lat,
              m: `${c.city}(${c.count})`,
              _circleSize: 30, _fillColor: '#FFB347', _textColor: '#084081',
            });
          });
        } else {
          this._cityClusters.forEach(c => {
            display.push({
              j: c.lng, w: c.lat,
              m: `${c.city}(${c.count})`,
              _circleSize: 30, _fillColor: '#FFB347', _textColor: '#084081',
            });
          });
        }
      }
      this._clusterLevel = zoom < 5 ? 'province' : 'city';
      this._clusterDisplay = display;
      this.renderClusterLayers(display);
    },
    // 渲染聚合图层（圆点 + 文字标签）
    renderClusterLayers(display) {
      if (!this.mapScene) return;
      // 移除旧聚合图层
      if (this.clusterLayer) {
        this.mapScene.removeLayer(this.clusterLayer);
        this.clusterLayer = null;
      }
      if (this.clusterLabelLayer) {
        this.mapScene.removeLayer(this.clusterLabelLayer);
        this.clusterLabelLayer = null;
      }
      if (!display || !display.length) return;
      // 圆点图层
      this.clusterLayer = new PointLayer({ zIndex: 5 })
        .source(display, { parser: { type: 'json', x: 'j', y: 'w' } })
        .shape('circle')
        .size('_circleSize')
        .color('_fillColor')
        .style({ opacity: 0.7, stroke: '#fff', strokeWidth: 1.5, strokeOpacity: 0.8 });
      this.clusterLayer.on('click', (e) => {
        // 点击聚合圆：放大到展开级别（targetZoom 为 L7 刻度）
        const zoom = this.mapScene.getZoom();
        if (zoom < 10) {
          // 省级圆放大到城市级(5)，城市圆放大到单车级(10)
          const targetZoom = zoom < 5 ? 5 : 10;
          // JSON parser 数据源 e.feature 即数据行本身（无 properties 包装）
          const props = e.feature?.properties || e.feature || {};
          const lng = props.j;
          const lat = props.w;
          // L7 Scene 没有 setZoomAndCenter，用 AMap 原生方法
          // 高德 zoom 比 L7 zoom 大 1（ZOOM_OFFSET），需 +1 转换
          const amap = this.mapScene.map;
          if (amap && amap.setZoomAndCenter) {
            amap.setZoomAndCenter(targetZoom + 1, [lng, lat]);
          } else {
            this.mapScene.setZoom(targetZoom);
            this.mapScene.setCenter([lng, lat]);
          }
        }
      });
      this.mapScene.addLayer(this.clusterLayer);
      // 文字标签图层
      this.clusterLabelLayer = new PointLayer({ zIndex: 6 })
        .source(display, { parser: { type: 'json', x: 'j', y: 'w' } })
        .shape('m', 'text')
        .size(13)
        .color('_textColor')
        .style({
          textAnchor: 'center',
          textOffset: [0, -5],
          spacing: 1,
          padding: [2, 2],
          stroke: '#ffffff',
          strokeWidth: 2.5,
          strokeOpacity: 1.0,
        });
      this.mapScene.addLayer(this.clusterLabelLayer);
    },
    // 仅切换聚合/单车可见性（轻量，zoomchange 时调用）
    updateClusterView() {
      if (!this.mapScene) return;
      const zoom = this.mapScene.getZoom();
      const shouldCluster = zoom < this.clusterZoomThreshold;
      if (shouldCluster !== this.isClusterView) {
        this.isClusterView = shouldCluster;
        this.applyClusterVisibility();
      }
      if (shouldCluster && this._cityClusters && this._cityClusters.length) {
        // 记录当前应处层级；重建延迟到 zoom 稳定后，避免动画帧中增删图层中断缩放动画
        this._clusterLevel = zoom < 5 ? 'province' : 'city';
        clearTimeout(this._clusterRebuildTimer);
        this._clusterRebuildTimer = setTimeout(() => {
          if (this.isClusterView && this._cityClusters && this._cityClusters.length) {
            this.rebuildClusterLayers();
          }
        }, 300);
      }
    },
    // 切换聚合图层与单车图层可见性（L7 用 show()/hide()）
    applyClusterVisibility() {
      const showCluster = this.isClusterView;
      // 聚合图层的构建由 updateClusterView 延迟到 zoom 稳定后执行，避免中断动画
      // 聚合图层显隐
      if (this.clusterLayer) showCluster ? this.clusterLayer.show() : this.clusterLayer.hide();
      if (this.clusterLabelLayer) showCluster ? this.clusterLabelLayer.show() : this.clusterLabelLayer.hide();
      // 单车图层显隐
      if (this.pointLayer) showCluster ? this.pointLayer.hide() : this.pointLayer.show();
      if (this.labelLayer) showCluster ? this.labelLayer.hide() : this.labelLayer.show();
      if (this.selectedCircleLayer) showCluster ? this.selectedCircleLayer.hide() : this.selectedCircleLayer.show();
    }
  }
}
</script>
<style lang="less" scoped>
/deep/ .ant-table-fixed-header .ant-table-scroll .ant-table-header {
  padding-bottom: 4px;
}

// ==================== 页面整体（G7 风格：浅灰底 + 蓝色主色） ====================
// 整体高度自适应：铺满可视区，地图吃掉剩余空间，列表固定在下方
// 具体高度由 JS 动态计算（pageHeight），避免固定值导致底部被挤出可视区
.map-page {
  position: relative;
  padding: 12px;
  min-height: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// a-spin 包裹层需要参与 flex 伸缩，否则地图无法撑开
.map-spin {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;

  ::v-deep .ant-spin-container {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

// 顶部分类 tab
.map-tabs {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;

  .map-tab-item {
    display: flex;
    align-items: center;
    height: 34px;
    padding: 0 16px;
    margin-right: 8px;
    cursor: pointer;
    user-select: none;
    font-size: 14px;
    color: #4e5969;
    background: #fff;
    border: 1px solid #dfe4ed;
    border-radius: 2px;
    transition: all 0.2s;

    &:hover {
      color: #1F6FEB;
      border-color: #9dc1fb;
    }

    .tab-count {
      margin-left: 6px;
      padding: 0 6px;
      min-width: 22px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      font-size: 12px;
      color: #4e5969;
      background: #f0f2f5;
      border-radius: 9px;
    }

    &.active {
      color: #fff;
      background: #1F6FEB;
      border-color: #1F6FEB;

      .tab-count {
        color: #fff;
        background: rgba(255, 255, 255, 0.25);
      }
    }
  }
}

// 地图容器
.map-wrapper {
  position: relative;
  border: 1px solid #dfe4ed;
  border-radius: 2px;
  overflow: hidden;
  background: #fff;
  // 自适应：占满 a-spin 剩余高度
  flex: 1 1 auto;
  min-height: 0;
}

.map-box {
  // 高度完全由父容器（flex 剩余空间）决定
  height: 100%;
  min-height: 750px;
  position: relative;
}

// 列表卡片：高度固定不参与伸缩
.list-card {
  flex: 0 0 auto;

  .query-row {
    flex: 0 0 auto;
    padding: 12px 12px 4px;
    border-bottom: 1px solid #f0f2f5;

    // 窄屏换行时，各条件之间保留纵向间距
    .ant-col {
      padding-bottom: 8px;
    }

    .query-col-btn {
      white-space: nowrap;
    }

    .query-col-actions {
      white-space: nowrap;
    }
  }

  // a-table title 区域紧凑化，承载状态统计条
  ::v-deep .ant-table-title {
    padding: 0;
    border-bottom: 1px solid #f0f2f5;
  }

  // 铺满模式：不隐藏地图，卡片绝对定位覆盖在地图上方；
  // 地图始终保持尺寸正常渲染，避免 display:none 导致 L7/WebGL 清空后返回空白
  &.list-fullscreen {
    position: absolute;
    top: 56px; // 容器 padding-top(12) + tab 高(34) + tab 下边距(10)
    right: 12px;
    bottom: 12px;
    left: 12px;
    z-index: 30;
    display: flex;
    flex-direction: column;

    // a-table 各层包裹器纵向拉伸，让固定表头表格填满卡片
    ::v-deep .ant-table-wrapper,
    ::v-deep .ant-spin-nested-loading,
    ::v-deep .ant-spin-container {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }
}

// 车辆属性文字：自有车 / 包车 / 临调 颜色区分
.own-type-text {
  font-weight: 600;

  // 自有车：蓝
  &.is-self {
    color: #13dc27;
  }

  // 包车：橙
  &.is-charter {
    color: #f38c18;
  }

  // 临调：紫
  &.is-temp {
    color: #722ed1;
  }
}

// 查询区左侧折叠按钮
.collapse-btn {
  min-width: auto;
  color: #1976d2 !important;
  font-weight: 600;

  ::v-deep .v-icon {
    color: #1976d2;
  }
}

// 折叠后地图左下角的展开入口
.list-expand-btn {
  position: absolute;
  left: 12px;
  bottom: 35px;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  background: #fff;
  border: 1px solid #9dc1fb;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: #e8f1ff;
  }
}

// 列表车辆状态统计条（G7 风格）
.table-stat-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: #fafbfc;
  font-size: 13px;

  .stat-split {
    width: 1px;
    height: 14px;
    margin: 0 10px;
    background: #e0e3e8;
  }

  .stat-item {
    display: inline-flex;
    align-items: center;
    margin-right: 16px;
    padding: 2px 8px;
    color: #4e5969;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s;

    em {
      margin-left: 5px;
      font-style: normal;
      font-weight: 600;
      color: #1a2233;
    }

    &:hover {
      background: #eef3fb;
    }

    &.active {
      color: #1F6FEB;
      background: #e8f1ff;

      em {
        color: #1F6FEB;
      }
    }

    // 状态色点
    .stat-dot {
      width: 8px;
      height: 8px;
      margin-right: 5px;
      border-radius: 2px;
      background: #c9cdd4;
    }

    &.is-running .stat-dot {
      background: #00b42a;
    }

    &.is-stop .stat-dot {
      background: #ff7d00;
    }

    &.is-offline .stat-dot {
      background: #86909c;
    }
  }

  .stat-all {
    font-weight: 600;
    color: #1a2233;
  }
}

// ==================== 运力汇总（表格视图） ====================
// 汇总卡片占据地图让出的剩余空间
.summary-card {
  flex: 1 1 auto;
  min-height: 0;
}

// 城市 tab：合计 + 18 地市（胶囊样式）
.summary-city-tabs {
  display: flex;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f5;

  .city-tab-item {
    display: inline-flex;
    align-items: center;
    height: 28px;
    padding: 0 12px;
    margin: 0 8px 6px 0;
    font-size: 13px;
    color: #4e5969;
    background: #f5f6f8;
    border: 1px solid transparent;
    border-radius: 14px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    em {
      margin-left: 5px;
      font-style: normal;
      font-size: 12px;
      color: #86909c;
    }

    &:hover {
      color: #1F6FEB;
      background: #e8f1ff;
    }

    &.active {
      color: #fff;
      background: #1F6FEB;

      em {
        color: #fff;
      }
    }
  }
}

// 业务状态切换条：全部 + 8 种状态
.summary-state-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 12px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;

  .state-item {
    display: inline-flex;
    align-items: center;
    margin-right: 14px;
    padding: 2px 8px;
    color: #4e5969;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s;

    em {
      margin-left: 5px;
      font-style: normal;
      font-weight: 600;
      color: #1a2233;
    }

    &:hover {
      background: #eef3fb;
    }

    &.active {
      color: #1F6FEB;
      background: #e8f1ff;

      em {
        color: #1F6FEB;
      }
    }

    .state-dot {
      width: 8px;
      height: 8px;
      margin-right: 5px;
      border-radius: 2px;
      background: #c9cdd4;
    }

    // 8 种业务状态色点（与表格 a-tag 配色对应）

    // 等待任务
    &.bs-0 .state-dot {
      background: pink;
    }

    // 靠位装车
    &.bs-1 .state-dot {
      background: #165dff;
    }

    // 发车
    &.bs-2 .state-dot {
      background: #13c2c2;
    }

    // 到达待卸
    &.bs-3 .state-dot {
      background: #fa8c16;
    }

    // 卸车
    &.bs-4 .state-dot {
      background: #faad14;
    }

    // 空车
    &.bs-5 .state-dot {
      background: #86909c;
    }

    // 空放
    &.bs-6 .state-dot {
      background: #722ed1;
    }

    // 充电
    &.bs-7 .state-dot {
      background: #52c41a;
    }

    &.bs-8 .state-dot {
      background: #f5222d;
    }

    // 维修
  }
}

.vehicle-info-panel {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1000;
  width: 320px;
  max-width: calc(100% - 32px);
  max-height: calc(100% - 32px);
  overflow-y: auto;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 6px 20px rgba(15, 40, 80, 0.18);
  font-size: 13px;
  color: #1a2233;

  .vehicle-info-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 0 12px;
    background: linear-gradient(90deg, #1F6FEB 0%, #3E8BFF 100%);
    position: sticky;
    top: 0;
    z-index: 1;

    .header-left {
      display: flex;
      align-items: center;
    }

    .vehicle-plate {
      color: #fff;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    .vehicle-close {
      cursor: pointer;
      color: rgba(255, 255, 255, 0.75);
      font-size: 13px;

      &:hover {
        color: #fff;
      }
    }
  }

  .vehicle-info-body {
    padding: 10px 12px 12px;
  }

  // 状态行
  .status-bar {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    .status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      margin-right: 6px;
      background: #9aa4b2;

      &.is-running {
        background: #00B42A;
        box-shadow: 0 0 0 3px rgba(0, 180, 42, 0.15);
      }

      &.is-stop {
        background: #FF7D00;
        box-shadow: 0 0 0 3px rgba(255, 125, 0, 0.15);
      }

      &.is-offline {
        background: #9aa4b2;
      }
    }

    .status-text {
      font-size: 13px;
      font-weight: 600;
      color: #4e5969;

      &.is-running {
        color: #00B42A;
      }

      &.is-stop {
        color: #FF7D00;
      }

      &.is-offline {
        color: #9aa4b2;
      }
    }

    .status-attr {
      margin-left: auto;
      padding: 0 6px;
      height: 20px;
      line-height: 20px;
      font-size: 12px;
      color: #1F6FEB;
      background: #eaf2ff;
      border-radius: 2px;
    }
  }

  // 关键指标
  .metric-group {
    display: flex;
    align-items: center;
    padding: 10px 0;
    margin-bottom: 10px;
    background: #f6f9fd;
    border-radius: 4px;

    .metric-item {
      flex: 1;
      text-align: center;
    }

    .metric-divider {
      width: 1px;
      height: 26px;
      background: #e2e9f2;
    }

    .metric-value {
      font-size: 20px;
      font-weight: 600;
      line-height: 1.1;
      color: #1F6FEB;

      em {
        font-style: normal;
        font-size: 12px;
        font-weight: 400;
        color: #86909c;
        margin-left: 2px;
      }
    }

    .metric-label {
      margin-top: 2px;
      font-size: 12px;
      color: #86909c;
    }
  }

  // 基础信息两列栅格
  .info-grid {
    display: flex;
    flex-wrap: wrap;

    .info-cell {
      width: 33.3%;
      display: flex;
      flex-direction: column;
      padding: 4px 0;

      &.full {
        width: 100%;
      }

      .info-label {
        font-size: 12px;
        color: #86909c;
        line-height: 18px;
      }

      .info-value {
        font-size: 13px;
        color: #1a2233;
        line-height: 20px;
        word-break: break-all;

        &.strong {
          font-weight: 600;
        }
      }
    }
  }

  // 位置区块
  .info-address {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #e2e9f2;

    .address-head {
      display: flex;
      align-items: center;
      margin-bottom: 4px;

      .address-time {
        margin-left: 4px;
        font-size: 12px;
        color: #86909c;
      }
    }

    .address-title {
      font-size: 13px;
      font-weight: 600;
      color: #1a2233;
      line-height: 20px;
    }

    .address-text {
      font-size: 12px;
      color: #4e5969;
      line-height: 18px;
      word-break: break-all;
    }
  }
}

/deep/ ul {
  padding-left: 0 !important;
}
/deep/ .ant-table-scroll{overflow-x:auto;}
p {
  margin: 0 !important;
  padding: 0 !important;
}
</style>
